import type { RibbonScalingStep } from '@1js/acui-ribbon-like';
import { computedFn } from 'mobx-utils/lib/computedFn';
import {
    changeGroupLayout,
    convertGroupToFlyout,
    dropLabel,
    dropPreviewItemFromGallery,
    moveToOverflowWell,
} from 'owa-command-ribbon';
import type { ComposeViewState } from 'owa-mail-compose-store';
import type { RibbonControlId } from 'owa-ribbon-ids';
import { MailRibbonTabId, MailRibbonGroupId, MailRibbonControlId } from 'owa-ribbon-ids';
import type { MailRibbonGroup } from 'owa-mail-ribbon-store-shared-types';
import { LabelPreferenceId } from 'owa-mail-ribbon-store-shared-types';
import { getComposeRibbonId } from 'owa-editor/lib/utils/getComposeRibbonId';
import { modifyIdsForCompose } from './modifyIdsForCompose';

/**
 * MLR scaling can be pretty complex. At this time, we haven't created a general means to
 * represent it.  For now, we'll create the scaling steps "by hand"
 */
export const getMLRScalingSteps = computedFn((tab: MailRibbonTabId): RibbonScalingStep[] => {
    let scalingSteps: RibbonScalingStep[] = new Array();

    switch (tab) {
        case MailRibbonTabId.Tab_Home:
            scalingSteps.push(changeGroupLayout(MailRibbonGroupId.Group_Tags));
            scalingSteps.push(changeGroupLayout(MailRibbonGroupId.Group_Move));
            scalingSteps.push(
                dropLabel(MailRibbonControlId.IgnoreStopIgnore, MailRibbonControlId.JunkFlyout)
            );
            scalingSteps.push(dropPreviewItemFromGallery(MailRibbonControlId.QuickStepsGallery));
            scalingSteps.push(changeGroupLayout(MailRibbonGroupId.Group_Respond));
            scalingSteps.push(convertGroupToFlyout(MailRibbonGroupId.Group_Tags));
            scalingSteps.push(convertGroupToFlyout(MailRibbonGroupId.Group_Move));
            scalingSteps.push(convertGroupToFlyout(MailRibbonGroupId.Group_Groups));
            scalingSteps.push(convertGroupToFlyout(MailRibbonGroupId.Group_QuickSteps));
            scalingSteps.push(convertGroupToFlyout(MailRibbonGroupId.Group_Find));
            break;

        default:
            break;
    }

    return scalingSteps;
});

/**
 * Converts the saved ordering of RibbonIds to a series of scaling action functions
 * (drop label or move to overflow well).
 * @returns a series of RibbonScalingStep that represent the order in which scaling
 * should occur
 */
export const getScalingSteps = computedFn(
    (
        storedRibbonConfig: MailRibbonGroup[],
        showLabelsPreference: LabelPreferenceId,
        controlsDontMoveToOverflow: RibbonControlId[],
        controlsDontDropLabels: RibbonControlId[],
        viewState: ComposeViewState | undefined
    ): RibbonScalingStep[] => {
        let scalingSteps: RibbonScalingStep[] = new Array();

        /**
         * In the below for loop we are iterating from the end to the beginning of the scalingConfig
         * array as we want to drop the labels on the right-most group's buttons first. The right-most
         * group's buttons were those that were added last to the scalingConfig array last. This will
         * drop labels from the right-most group, and all the elements within the group as the same time.
         */
        if (showLabelsPreference === LabelPreferenceId.ShowAsSpacePermits) {
            for (let groupIndex = storedRibbonConfig.length - 1; groupIndex >= 0; groupIndex--) {
                const perGroupRibbonIds: RibbonControlId[] =
                    storedRibbonConfig[groupIndex].controlIds;

                //some controls we never want to drop labels for
                const controlsThatDropLabels: RibbonControlId[] = perGroupRibbonIds.filter(
                    control => !controlsDontDropLabels.includes(control)
                );

                //drops labels for all controls within the group at groupIndex, simultaneously
                if (controlsThatDropLabels.length > 0) {
                    scalingSteps.push(
                        dropLabel(...modifyIdsForCompose(controlsThatDropLabels, viewState))
                    );
                }
            }
        }

        //then each control, individually, should move to overflow well
        for (let groupIndex = storedRibbonConfig.length - 1; groupIndex >= 0; groupIndex--) {
            const perGroupRibbonIds: RibbonControlId[] = storedRibbonConfig[groupIndex].controlIds;
            //iterates through every control from right to left and adds a move to overflow scaling step
            for (let i = perGroupRibbonIds.length - 1; i >= 0; i--) {
                if (!controlsDontMoveToOverflow.includes(perGroupRibbonIds[i])) {
                    const idToMove: RibbonControlId | string = viewState
                        ? getComposeRibbonId(perGroupRibbonIds[i], viewState)
                        : perGroupRibbonIds[i];
                    scalingSteps.push(moveToOverflowWell(idToMove));
                }
            }
        }

        return scalingSteps;
    }
);
